function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useState } from 'react';
import { Dropdown, FormControl, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/**
 * @param Object params - { page: String, query: String }
 * @param function setParams(params) - sets the params in the parent component
 * @param {Array} filters - [name: String, label: String, type: 'text' | 'select', options: [String]]
 * @
 *
 * Renders a filter component which contains field for each filter.
 * Changing the value of the filter will update the params in the parent component.
 * 
 * For select filters, we need to pass options as key strings as we translate them inside select component.
 */
export default function Filters(_ref) {
  let {
    params,
    setParams,
    filters
  } = _ref;
  const {
    t
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const onFilterToggle = (isOpen, metadata) => {
    if (metadata.target.localName === 'input' || metadata.target.localName === 'select') return;
    setIsOpen(isOpen);
  };

  // open filter menu if there are params in the url
  useEffect(() => {
    if (!!params.query) {
      setIsOpen(true);
    }
  }, [params.query]);

  // Dropdown needs access to the DOM node in order to position the Menu
  const CustomToggle = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
    let {
      children,
      onClick
    } = _ref2;
    return /*#__PURE__*/React.createElement(Button, {
      ref: ref,
      onClick: e => {
        e.preventDefault();
        onClick(e);
      }
    }, children, /*#__PURE__*/React.createElement("span", {
      className: "material-icons md-18 ms-2"
    }, isOpen ? 'remove' : 'add'));
  });

  // Dropdown needs access to the DOM of the Menu to measure it
  const CustomMenu = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
    let {
      children,
      style,
      className,
      'aria-labelledby': labeledBy
    } = _ref3;
    const [filterValues, setFilterValues] = useState({});
    const handleFilterChange = (column, value) => {
      setFilterValues(prevFilters => _objectSpread(_objectSpread({}, prevFilters), {}, {
        [column]: encodeURIComponent(value)
      }));
    };
    const onFilterSubmit = () => {
      setParams(_objectSpread(_objectSpread({}, params), {}, {
        query: new URLSearchParams(filterValues).toString()
      }));
    };
    const onFilterClear = () => {
      setFilterValues({});
      setParams(_objectSpread(_objectSpread({}, params), {}, {
        query: ''
      }));
    };
    const getFilterFormControl = filter => {
      switch (filter.type) {
        case 'select':
          return /*#__PURE__*/React.createElement(FormControl, {
            as: "select",
            className: "mx-3 my-2 w-auto",
            onChange: e => handleFilterChange(filter.name, e.target.value),
            value: decodeURIComponent(filterValues[filter.name] || '')
          }, /*#__PURE__*/React.createElement("option", {
            value: ""
          }), filter.options.map((option, index) => {
            return /*#__PURE__*/React.createElement("option", {
              key: index,
              value: option
            }, t(option));
          }));
        default:
          return /*#__PURE__*/React.createElement(FormControl, {
            className: "mx-3 my-2 w-auto",
            onChange: e => handleFilterChange(filter.name, e.target.value),
            value: decodeURIComponent(filterValues[filter.name] || '')
          });
      }
    };

    // if there are params in url, set the filter values to the params
    useEffect(() => {
      if (params.query) {
        const query = new URLSearchParams(params.query);
        const filters = {};
        for (const [key, value] of query.entries()) {
          filters[key] = value;
        }
        setFilterValues(filters);
      }
    }, [params]);
    return /*#__PURE__*/React.createElement("div", {
      ref: ref,
      style: style,
      className: className,
      "aria-labelledby": labeledBy
    }, /*#__PURE__*/React.createElement(Button, {
      className: "float-end",
      variant: "link",
      style: {
        'width': '100px'
      },
      onClick: () => onFilterClear()
    }, "Clear All"), /*#__PURE__*/React.createElement("div", {
      className: "d-flex flex-column"
    }, filters.map((filter, index) => {
      return /*#__PURE__*/React.createElement("div", {
        key: index,
        className: "d-flex flex-column mt-2"
      }, /*#__PURE__*/React.createElement("label", {
        className: "font-weight-bold"
      }, filter.label), getFilterFormControl(filter));
    })), /*#__PURE__*/React.createElement(Button, {
      className: "float-center",
      variant: "primary",
      style: {
        'width': '100px'
      },
      onClick: () => onFilterSubmit()
    }, "Submit"));
  });
  return /*#__PURE__*/React.createElement(Dropdown, {
    show: isOpen,
    onToggle: (isOpen, metadata) => onFilterToggle(isOpen, metadata)
  }, /*#__PURE__*/React.createElement(Dropdown.Toggle, {
    as: CustomToggle,
    id: "dropdown-custom-components"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18 me-2"
  }, "tune"), " Filters"), /*#__PURE__*/React.createElement(Dropdown.Menu, {
    as: CustomMenu,
    className: "filters-menu d-flex flex-column"
  }));
}